import * as Sentry from '@sentry/react';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { isEmpty } from 'lodash';
import { ShippingMethod } from '~api/selfcare';
import { FetchPickupLocationsRequest, GetShipmentResponse, Shipment } from '~api/som';
import { FetchShipmentMetadataResponse, Filtering, ListShipmentsSummariesRequest } from '~api/som_search';
import { Order } from '~api/tos';

import { ConfigApiInstance, SomApiInstance, SomSearchApiInstance, WimoApiInstance } from '~src/main/api';
import { useSiteId } from '~src/main/hooks/useSiteId';
import { useShippingMethodListQuery, useShippingMethodListSuspenseQuery } from '~src/main/queries';
import { createContactFromAddress } from '~src/utils/create-contact-from-address';

import { useTransportOrderDetailsSuspenseQuery } from '../order/queries';
import { CarrierContractDetail, useCarrierContractQuery } from '../settings/carrier-contract/queries';
import { GroupType } from './list/groups';
import { ShipmentDetails } from './types';
import { getDeliveries } from './utils';

export type ShipmentListItem = ReturnType<typeof useShipmentList>['data'][0];

type ShipmentsListQueryParams = {
  pageIndex?: number;
  pageSize?: number;
} & ListShipmentsSummariesRequest;

const isFulfilled = <T>(p: PromiseSettledResult<T>): p is PromiseFulfilledResult<T> => p.status === 'fulfilled';

export function usePickupLocationQueryKey() {
  const siteId = useSiteId();

  const keys = {
    all: ['pickupLocation', siteId] as const,
    lists: () => [...keys.all, 'list'] as const,
    list: (filters: FetchPickupLocationsRequest) => [...keys.lists(), filters] as const,
  };

  return keys;
}

export function useShipmentQueryKey() {
  const siteId = useSiteId();

  const keys = {
    all: ['shipments', siteId] as const,
    lists: () => [...keys.all, 'list'] as const,
    list: (filters: ShipmentsListQueryParams) => [...keys.lists(), filters] as const,
    count: (filters: Filtering) => [...keys.lists(), 'count', { filtering: filters }],
    details: () => [...keys.all, 'detail'] as const,
    detail: (id?: string) => [...keys.details(), id] as const,
  };

  return keys;
}

export function useCarrierAddonQueryKey() {
  const siteId = useSiteId();

  const keys = {
    all: ['carrierAddons', siteId] as const,
    lists: () => [...keys.all, 'list'] as const,
  };

  return keys;
}

export function useShipmentMetadataFieldQueryKey() {
  const siteId = useSiteId();

  const keys = {
    all: ['shipmentMetadataFields', siteId] as const,
    lists: () => [...keys.all, 'list'] as const,
    list: (filters: { shippingMethodId: string | undefined }) => [...keys.lists(), filters] as const,
  };

  return keys;
}

export function useShipmentMetadataQueryKey() {
  const baseQueryKey = useShipmentQueryKey();

  const keys = {
    all: [...baseQueryKey.all, 'metadata'] as const,
    details: () => [...keys.all, 'detail'] as const,
    detail: (id?: string) => [...keys.details(), id] as const,
  };

  return keys;
}

export function useShipmentTrackingEventQueryKey() {
  const baseQueryKey = useShipmentQueryKey();

  const keys = {
    all: [...baseQueryKey.all, 'trackingEvents'],
    lists: (deliveryIds: string[]) => [...keys.all, 'list', deliveryIds] as const,
  };

  return keys;
}

export function useShipmentsListQuery(query: ShipmentsListQueryParams, enabled = true) {
  const queryKey = useShipmentQueryKey();

  const pageIndex = query.pageIndex ?? 0;
  const pageSize = query.pageSize ?? 10;

  return useQuery({
    queryFn: async () => {
      const { data } = await SomSearchApiInstance.shipmentsSummariesGet.somSearchListShipmentsSummaries({
        ...query,
        pagination: {
          limit: pageSize,
          offset: pageIndex * pageSize,
        },
      });
      return data.shipments ?? [];
    },
    enabled,
    queryKey: queryKey.list(query),
  });
}

export function useShipmentList(query: ShipmentsListQueryParams) {
  const shipmentListQuery = useShipmentsListQuery(query, true);
  const shippingMethodListQuery = useShippingMethodListQuery({ customBookingMethods: false, onlyConfigured: false });

  const methods = shippingMethodListQuery.data ?? [];
  const shipments = shipmentListQuery.data ?? [];

  return {
    isLoading: shipmentListQuery.isLoading || shippingMethodListQuery.isLoading,
    data: shipments.map(shipment => ({
      ...shipment,
      shipping_method: methods.find(m => m.id === shipment.shipping_method),
    })),
  };
}

export function useShipmentsCountQuery(params: Filtering) {
  const queryKey = useShipmentQueryKey();

  return useQuery({
    queryFn: async () => {
      const { data } = await SomSearchApiInstance.shipmentsCount.somSearchCountShipments({
        filtering: params,
      });

      const groups =
        data.counts?.map(c => ({
          group: c.group!.replace('_GROUP', '') as GroupType,
          count: c.count ?? 0,
        })) ?? [];

      return {
        groups,
      };
    },
    queryKey: queryKey.count(params),
  });
}

export async function fetchShipmentDetails({ id }: Pick<Shipment, 'id'>) {
  const { data } = await SomApiInstance.shipmentsGet.somGetShipment({ id });
  return data;
}

export function useShipmentQuery({ id }: Pick<Shipment, 'id'>) {
  const queryKey = useShipmentQueryKey();

  return useSuspenseQuery({
    queryFn: () => fetchShipmentDetails({ id }),
    queryKey: queryKey.detail(id),
  });
}

export const fetchShipmentMetadata = async (shipmentId?: string) => {
  const { data } = await SomSearchApiInstance.shipmentMetadataGet.somSearchFetchShipmentMetadata({
    shipment_id: shipmentId,
  });

  return data;
};

export function useShipmentMetadata({ id }: Pick<Shipment, 'id'>) {
  const queryKey = useShipmentMetadataQueryKey();

  return useSuspenseQuery({
    queryFn: () => fetchShipmentMetadata(id),

    // Metadata has its own query key for fine-grained control since the API is async.
    queryKey: queryKey.detail(id),

    // Query should only fail if the API has not caught up yet, when creating a new shipment for instance.
    retry: 5,
  });
}

export function toShipmentDetails(
  shipment: GetShipmentResponse,
  carrier_contract: CarrierContractDetail | undefined,
  metadata: FetchShipmentMetadataResponse,
  shipping_methods: ShippingMethod[],
  tos: Order | undefined
): ShipmentDetails {
  return {
    tos: tos ?? {},
    id: shipment.shipment?.id,
    addons: shipment.shipment?.addons,
    contents: shipment.shipment?.contents,
    direction_type: shipment.shipment?.direction_type,
    contact_from: createContactFromAddress('shipment', 'contact_from', shipment.shipment?.address_from),
    contact_to: createContactFromAddress('shipment', 'contact_to', shipment.shipment?.address_to),
    contact_return: createContactFromAddress('shipment', 'contact_return', shipment.shipment?.address_return),
    customs: shipment.shipment?.customs,
    customer_info: {
      ...shipment.shipment?.customer_info,
      contact: createContactFromAddress(
        'shipment',
        'contact_customer_info',
        shipment.shipment?.customer_info?.address,
        shipment.shipment?.customer_info?.phone,
        shipment.shipment?.customer_info?.email
      ),
    },
    shipping_method:
      shipping_methods.find(
        // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style
        s => s.id === (shipment.shipment?.parcels?.[0]?.deliveries?.[0].shipping_method as string)
      ) ?? {},
    carrier_contract: carrier_contract && {
      ...carrier_contract,
      carrier_configuration_data: JSON.stringify(carrier_contract.carrier_configuration_data),
    },
    delivery_time: shipment.shipment?.parcels?.[0]?.deliveries?.[0].delivery_time,
    shipping_date: shipment.shipment?.shipping_date,
    weight: shipment?.shipment?.parcels?.[0]?.weight,
    dimensions: shipment?.shipment?.parcels?.[0]?.dimensions,
    status: {
      type: metadata.status ?? 'UNKNOWN',
      message: metadata.status_message,
    },
    deliveries: getDeliveries(shipment.shipment?.parcels),
    external_id: shipment.shipment?.external_id,
    parcels: shipment.shipment?.parcels,
    customs_declaration: shipment.shipment?.customs?.declaration,
    location_ref: shipment.shipment?.parcels?.[0].deliveries?.[0]?.location_ref,
    location_type: shipment.shipment?.parcels?.[0].deliveries?.[0]?.location_type,
    created_at: shipment.shipment?.created_at,
    updated_at: shipment.shipment?.updated_at,
    meta: shipment.shipment?.meta,
  };
}

export function useShipmentDetailsQuery({ id }: Pick<Shipment, 'id'>): ShipmentDetails {
  const { data: shipment } = useShipmentQuery({ id });
  const { data: carrier_contract } = useCarrierContractQuery({ contract_id: shipment.shipment?.carrier_contract_id });
  const { data: metadata } = useShipmentMetadata({ id });
  const { data: shipping_methods } = useShippingMethodListSuspenseQuery({ customBookingMethods: false });
  const { data: tos } = useTransportOrderDetailsSuspenseQuery({ id: shipment?.shipment?.tos_id });

  return toShipmentDetails(shipment, carrier_contract, metadata, shipping_methods, tos);
}

export const fetchTrackingEvents = async (delivery_ids: string[]) => {
  const reqs = delivery_ids.map(async delivery_id => {
    const { data } = await WimoApiInstance.trackingEvents.wimoGetTrackingEvents({
      delivery_id,
    });

    return data;
  });

  const results = await Promise.allSettled(reqs);

  return results
    .filter(isFulfilled)
    .map(p => p.value)
    .filter(t => t.logistic_units && t.logistic_units.length > 0);
};

export function useGetTrackingEventsQuery({ delivery_ids }: { delivery_ids: string[] }) {
  const queryKey = useShipmentTrackingEventQueryKey();

  return useSuspenseQuery({
    queryFn: () => fetchTrackingEvents(delivery_ids),

    // Tracking events has its own query key for fine-grained control since the API is async.
    queryKey: queryKey.lists(delivery_ids),
  });
}

export function useCarrierAddonsQuery() {
  const queryKey = useCarrierAddonQueryKey();

  return useQuery({
    queryFn: async () => {
      const { data } = await ConfigApiInstance.carrieraddonsGet.configGetCarrierAddons();
      return data;
    },
    queryKey: queryKey.lists(),
  });
}

export function usePickupLocationQuery(payload: FetchPickupLocationsRequest) {
  const queryKey = usePickupLocationQueryKey();

  return useQuery({
    queryFn: async () => {
      try {
        const { data } = await SomApiInstance.fetchPickupLocations.somFetchPickupLocations(payload);
        return data.pickup_locations ?? [];
      } catch (e) {
        Sentry.captureException(e);

        if (isAxiosError(e)) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          throw new Error(e.response?.data?.error ?? 'failed to fetch pickup locations');
        }

        throw e;
      }
    },
    queryKey: queryKey.list(payload),
    throwOnError: false,
    enabled:
      !isEmpty(payload.address_from) &&
      !isEmpty(payload.address_to) &&
      !isEmpty(payload.shipping_method) &&
      !isEmpty(payload.pickup_time),
  });
}

interface ShippingMethodMetadataField {
  key: string;
}

/**
 * For the time being, this query is completely overkill, there's really no need for it.
 * However, there is a plan to expose the metadata schema via some kind of API.
 *
 * With that said, this query simplifies the migration towards that solution, once it's in place.
 */
export function useShipmentMetadataFieldQuery({ shippingMethodId }: { shippingMethodId: string | undefined }) {
  const keys = useShipmentMetadataFieldQueryKey();

  return useQuery<ShippingMethodMetadataField[]>({
    queryFn: () => {
      switch (shippingMethodId) {
        case 'gnc-std':
          return [{ key: 'label.from_phone' }, { key: 'gnc.transport_instruction_1' }];
        case 'bst-std':
          return [{ key: 'brn.product_name' }, { key: 'brn.product_route' }, { key: 'brn.delivery_date' }];
        case 'brn-blp':
        case 'brn-pip':
        case 'brn-ppi':
        case 'brn-phd':
          return [{ key: 'brn.additionalServiceCode' }];
        case 'dbs-par':
        case 'dbs-pos':
          return [
            { key: 'dbs.location_ref' },
            { key: 'dbs.payment_cost' },
            { key: 'dbs.gs1_prefix' },
            { key: 'dbs.consignors_ref' },
            { key: 'dbs.consignors_phone' },
            { key: 'dbs.return_tracking_number' },
            { key: 'dbs.merchant_id' },
          ];
        case 'tmr-noroute':
          return [{ key: 'tmr.customer_id' }, { key: 'tmr.routing_code' }];
        case 'dhl-esl':
        case 'dhl-eww':
        case 'dhl-fsp':
        case 'dhl-fpk':
        case 'dhl-prt':
        case 'dhl-sty':
          return [
            { key: 'dhl.customer_no' },
            { key: 'dhl.phone_from' },
            { key: 'dhl.paying_customer_no' },
            { key: 'dhl.routing_code' },
            { key: 'dhl.external_id' },
            { key: 'dhl.addon_codes' },
            { key: 'dhl.feature_code' },
          ];
        case 'tnt-exp':
        case 'tnt-exp12':
          return [{ key: 'tnt.customer_id' }];
        default:
          return [];
      }
    },
    queryKey: keys.list({ shippingMethodId }),
    enabled: !!shippingMethodId,
  });
}
